import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, ShieldOff, ShieldCheck } from 'lucide-react';
import { API_URL } from './shared';

const PlannerOperatorsTab = ({ token }) => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [granting, setGranting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/admin/planner/operators`, { headers: { Authorization: `Bearer ${token}` } });
      setOperators(r.data || []);
    } catch (e) { toast.error('Failed to load operators'); }
  };

  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [token]);

  const grant = async () => {
    if (!email) { toast.error('Enter an email'); return; }
    setGranting(true);
    try {
      await axios.post(`${API_URL}/admin/planner/operators/grant-by-email`, { email }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Operator role granted');
      setEmail('');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setGranting(false); }
  };

  const toggle = async (id) => {
    try { await axios.post(`${API_URL}/admin/planner/operators/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } }); await load(); }
    catch (e) { toast.error('Failed'); }
  };

  const disable = async (id) => {
    if (!window.confirm('Disable operator access for this user?')) return;
    try { await axios.post(`${API_URL}/admin/planner/operators/${id}/disable`, {}, { headers: { Authorization: `Bearer ${token}` } }); await load(); toast.success('Disabled'); }
    catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0A2342]" /></div>;

  return (
    <div className="space-y-4" data-testid="planner-operators-tab">
      <div>
        <h2 className="text-lg font-semibold">Dive Planner Operators</h2>
        <p className="text-sm text-gray-500">Grant or revoke operator access to the Dive Planner. Operators can create and manage their own trips at <code>/planner</code>.</p>
      </div>

      <div className="bg-white border rounded-lg p-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <Input placeholder="user@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="grant-operator-email" />
        <Button onClick={grant} disabled={granting} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="grant-operator-btn">
          {granting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Grant
        </Button>
      </div>

      {operators.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center text-sm text-gray-500">No operators yet. Grant access using the form above.</div>
      ) : (
        <div className="bg-white border rounded-lg divide-y">
          {operators.map((u) => (
            <div key={u.id} className="p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-[#0A2342] truncate">{u.first_name} {u.last_name} <span className="text-gray-400 text-sm">· {u.email}</span></div>
                <div className="text-xs text-gray-500">
                  {u.trip_count} trip{u.trip_count === 1 ? '' : 's'}
                  {u.is_operator ? <span className="ml-2 text-green-600">Active</span> : <span className="ml-2 text-red-500">Disabled</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => toggle(u.id)} data-testid={`toggle-operator-${u.id}`}>
                  {u.is_operator ? <ShieldOff className="w-4 h-4 mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
                  {u.is_operator ? 'Revoke' : 'Re-grant'}
                </Button>
                {u.is_operator && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => disable(u.id)}>Disable</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlannerOperatorsTab;
